import Sdk from 'api.digitalpages.module.sdk.api';
import AuthorService from './AuthorService';

const CACHE_KEYS = {
  favoritos: 'view_favoritos_cache',
  marcacoes: 'view_marcacoes_cache',
  listas: 'view_listas_de_leitura_cache',
};

function saveToCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // localStorage cheio ou indisponível
  }
}

async function getPaginatedUserData(url) {
  const result = [];
  let page = 1;
  while (true) {
    const response = await Sdk.Api.dynamic.bridge(`${url}?page=${page}`, null, 'GET');
    result.push(...response.result);
    if (!response.next_page_query) break;
    page++;
  }
  return result;
}

function toUserData(serverData) {
  return {
    uid: serverData.uid,
    contextType: serverData.context_type,
    contextUid: serverData.context_uid,
    targetType: serverData.target_type,
    targetUid: serverData.target_uid,
    tags: serverData.tags?.map(t => ({ uid: t.uid, name: t.name })),
  };
}

async function getContentDetail(uid) {
  try {
    const url = `cms/v1.1/managed/content/group/uid/${uid}`;
    const response = await Sdk.Api.dynamic.bridge(url, null, 'GET');
    return {
      uid: response.uid,
      title: response.name || 'Título não disponível',
      author: response.properties?.find(p =>
        p.name === 'Autor' || p.name === 'autor' || p.name === 'Author' || p.name === 'AUTOR'
      )?.value || 'Autor não informado',
      description: response.properties?.find(p => p.name === 'Descrição')?.value,
      image: `${Sdk.Api.domain}/cms/v1.1/managed/content/group/uid/${uid}/default/thumb/v2?image_options=width:200&api_env=${Sdk.Api.enviroment}&project_key=${Sdk.Api.projectKey}&access_token=${Sdk.Api.authorization.credential.accessToken}`,
    };
  } catch {
    return null;
  }
}

async function prefetchFavorites() {
  const [authors, allUserData] = await Promise.all([
    AuthorService.loadAuthors(),
    getPaginatedUserData('cms/v1.1/user/data'),
  ]);

  const favoriteData = allUserData
    .map(toUserData)
    .filter(p =>
      p.tags?.length > 0 &&
      p.targetType === 'ManagedContent' &&
      p.contextType === 'ManagedContent' &&
      p.tags.some(t => t.name === '###InternalFavorites###')
    );

  const contentsUids = [...new Map(
    favoriteData.map(p => [p.contextUid, p.contextUid])
  ).values()];

  const favoriteContents = (await Promise.all(
    contentsUids.map(uid => getContentDetail(uid))
  )).filter(Boolean);

  saveToCache(CACHE_KEYS.favoritos, { favorites: favoriteContents, authors });
}

async function prefetchAppointments() {
  const [authors, allUserData] = await Promise.all([
    AuthorService.loadAuthors(),
    getPaginatedUserData('cms/v1.1/user/data'),
  ]);

  const appointmentData = allUserData
    .map(toUserData)
    .filter(p =>
      p.targetType === 'ManagedContentReference' &&
      p.contextType === 'ManagedContent'
    );

  const marksMap = new Map();
  appointmentData.forEach(item => {
    const existing = marksMap.get(item.contextUid) || { uid: item.contextUid, numberMark: 0 };
    existing.numberMark++;
    marksMap.set(item.contextUid, existing);
  });

  const appointments = (await Promise.all(
    [...marksMap.values()].map(async mark => {
      const content = await getContentDetail(mark.uid);
      if (!content) return null;
      return { ...content, appointmentsLength: mark.numberMark };
    })
  )).filter(Boolean);

  saveToCache(CACHE_KEYS.marcacoes, { appointments });
}

async function prefetchReadingLists() {
  const [tags, sharedTags, userData, userDataShared] = await Promise.all([
    Sdk.Api.dynamic.bridge('cms/v1.1/user/data/tags', null, 'GET'),
    (async () => {
      const response = await Sdk.Api.dynamic.bridge('cms/v1.1/user/data/tags/shared', null, 'GET');
      return response.filter(p => p.tag.active_share).map(p => ({
        uid: p.tag.uid,
        name: p.tag.name,
        shareCode: p.tag.code_share,
        shareEnabled: p.tag.active_share,
      }));
    })(),
    getPaginatedUserData('cms/v1.1/user/data'),
    getPaginatedUserData('cms/v1.1/user/data/shared'),
  ]);

  const allUserData = [...userData, ...userDataShared]
    .map(toUserData)
    .filter(p =>
      p.tags?.length > 0 &&
      p.targetType === 'ManagedContent' &&
      p.contextType === 'ManagedContent'
    );

  const uniqueTags = [
    ...tags.map(t => ({
      uid: t.uid,
      name: t.name,
      shareCode: t.code_share,
      shareEnabled: t.active_share,
    })),
    ...sharedTags,
  ].filter(p => p.name !== '###InternalFavorites###');

  const contentsMap = {};
  for (const item of allUserData) {
    for (const tag of item.tags || []) {
      if (!contentsMap[tag.uid]) contentsMap[tag.uid] = [];
      const content = await getContentDetail(item.contextUid);
      if (content) contentsMap[tag.uid].push(content);
    }
  }

  const readingLists = uniqueTags.map(tag => ({
    uid: tag.uid,
    title: tag.name,
    editable: true,
    isPublic: tag.shareEnabled || false,
    shareCode: tag.shareCode || '',
    books: contentsMap[tag.uid] || [],
  }));

  saveToCache(CACHE_KEYS.listas, { readingLists });
}

export async function prefetchLibraryData() {
  try {
    await Promise.all([
      prefetchFavorites(),
      prefetchAppointments(),
      prefetchReadingLists(),
    ]);
  } catch (error) {
    console.error('[prefetchLibraryData] Error:', error);
  }
}
